import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

type Prefs = {
  showFurigana: boolean;
  showGlosses: boolean;
  setShowFurigana: (v: boolean) => void;
  setShowGlosses: (v: boolean) => void;
};

const STORAGE_KEY = 'displayPrefs';

const DisplayPrefsContext = createContext<Prefs | null>(null);

type Persisted = { showFurigana: boolean; showGlosses: boolean };

function load(): Persisted {
  if (typeof localStorage === 'undefined') return { showFurigana: true, showGlosses: false };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { showFurigana: true, showGlosses: false };
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    return {
      showFurigana: parsed.showFurigana ?? true,
      showGlosses: parsed.showGlosses ?? false,
    };
  } catch {
    return { showFurigana: true, showGlosses: false };
  }
}

export function DisplayPrefsProvider({ children }: { children: ReactNode }) {
  const initial = load();
  const [showFurigana, setShowFurigana] = useState(initial.showFurigana);
  const [showGlosses, setShowGlosses] = useState(initial.showGlosses);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ showFurigana, showGlosses } satisfies Persisted),
    );
  }, [showFurigana, showGlosses]);

  return (
    <DisplayPrefsContext.Provider
      value={{ showFurigana, showGlosses, setShowFurigana, setShowGlosses }}
    >
      {children}
    </DisplayPrefsContext.Provider>
  );
}

export function useDisplayPrefs(): Prefs {
  const ctx = useContext(DisplayPrefsContext);
  if (!ctx) throw new Error('useDisplayPrefs must be used within DisplayPrefsProvider');
  return ctx;
}
